import type { MaybeRefOrGetter } from 'vue';

import { onMounted, onScopeDispose, toValue, watch } from 'vue';

const MOBILE_REFRESH_CLASS = 'mobile-custom-pull-to-refresh';

export interface ClassListTarget {
  classList: {
    toggle: (className: string, force?: boolean) => boolean;
  };
}

const consumerCounts = new WeakMap<ClassListTarget, number>();

const syncTargetClass = (target: ClassListTarget, count: number): void => {
  target.classList.toggle(MOBILE_REFRESH_CLASS, count > 0);
};

export const acquireMobileRefreshOverscroll = (target: ClassListTarget): (() => void) => {
  const nextCount = (consumerCounts.get(target) ?? 0) + 1;
  consumerCounts.set(target, nextCount);
  syncTargetClass(target, nextCount);
  let isReleased = false;

  return () => {
    if (isReleased) {
      return;
    }

    isReleased = true;
    const remainingCount = Math.max(0, (consumerCounts.get(target) ?? 1) - 1);
    if (remainingCount === 0) {
      consumerCounts.delete(target);
    } else {
      consumerCounts.set(target, remainingCount);
    }
    syncTargetClass(target, remainingCount);
  };
};

export interface MobileDataRefreshGate {
  isMobile: boolean;
  isAuthenticated: boolean;
  isContextChecked: boolean;
  hasOrganization: boolean;
  isRefreshing: boolean;
  isTenantTransitioning: boolean;
}

export const isMobileDataRefreshEnabled = (gate: MobileDataRefreshGate): boolean => {
  return (
    gate.isMobile &&
    gate.isAuthenticated &&
    gate.isContextChecked &&
    gate.hasOrganization &&
    !gate.isRefreshing &&
    !gate.isTenantTransitioning
  );
};

export interface ManualDataRefreshRunner<TOutcome> {
  run: () => Promise<TOutcome>;
}

export const createManualDataRefreshRunner = <TOutcome>({
  refresh,
  onActiveChange,
}: {
  refresh: () => Promise<TOutcome>;
  onActiveChange: (isActive: boolean) => void;
}): ManualDataRefreshRunner<TOutcome> => {
  let inFlight: Promise<TOutcome> | null = null;

  return {
    run: () => {
      if (inFlight) {
        return inFlight;
      }

      onActiveChange(true);
      const promise = refresh().finally(() => {
        onActiveChange(false);
        if (inFlight === promise) {
          inFlight = null;
        }
      });
      inFlight = promise;

      return promise;
    },
  };
};

export const useMobileRefreshOverscroll = (
  enabled: MaybeRefOrGetter<boolean>,
  getTarget: () => ClassListTarget = () => document.documentElement,
): void => {
  let release: (() => void) | null = null;
  let isMounted = false;

  const sync = (): void => {
    if (!isMounted) {
      return;
    }

    const shouldApply = toValue(enabled);
    if (shouldApply && !release) {
      release = acquireMobileRefreshOverscroll(getTarget());
    } else if (!shouldApply && release) {
      release();
      release = null;
    }
  };
  const stopWatch = watch(() => toValue(enabled), sync);

  onMounted(() => {
    isMounted = true;
    sync();
  });

  onScopeDispose(() => {
    stopWatch();
    release?.();
    release = null;
    isMounted = false;
  });
};
